/**
 * ResultSummaryCard Component
 *
 * Shows the main result summary on the SharedCalculation preview page.
 * Displays recommended tariff and potential savings.
 */

import { Card, Group, Title, Text, ThemeIcon, Stack, Badge, Divider } from '@mantine/core';
import { IconTrendingDown, IconCheck, IconBolt } from '@tabler/icons-react';

interface ResultSummaryCardProps {
  recommendedTariff: string;
  currentTariff: string;
  currentCost: number;
  recommendedCost: number;
  savings: number;
  savingsPercent: number;
}

export function ResultSummaryCard({
  recommendedTariff,
  currentTariff,
  currentCost,
  recommendedCost,
  savings,
  savingsPercent
}: ResultSummaryCardProps) {
  const hasSavings = savings > 0 && recommendedTariff !== currentTariff;

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Card shadow="md" radius="lg" p="xl" withBorder bg="ecoGreen.0">
      <Stack gap="lg">
        {/* Main recommendation */}
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Text size="sm" c="dimmed" mb={4}>Rekomendowana taryfa</Text>
            <Group gap="sm" align="center">
              <ThemeIcon size={48} radius="md" color="ecoGreen">
                <IconBolt size={28} />
              </ThemeIcon>
              <div>
                <Title order={1} c="ecoGreen.7">{recommendedTariff}</Title>
                {hasSavings && (
                  <Badge color="green" variant="light" size="sm">
                    <IconCheck size={12} style={{ marginRight: 4 }} />
                    Lepsza niż {currentTariff}
                  </Badge>
                )}
              </div>
            </Group>
          </div>

          <Stack ta="right" gap={0}>
            <Text size="sm" c="dimmed" mb={4}>Potencjalna oszczędność</Text>
            <Group gap="xs" justify="flex-end" align="baseline">
              {hasSavings ? (
                <>
                  <ThemeIcon size={32} radius="xl" color="green" variant="light">
                    <IconTrendingDown size={20} />
                  </ThemeIcon>
                  <Title order={1} c="green.7">{formatCurrency(savings)}</Title>
                  <Text size="lg" c="dimmed">/rok</Text>
                </>
              ) : (
                <>
                  <Title order={2} c="gray.6">0 zł</Title>
                  <Text size="sm" c="dimmed">/rok</Text>
                </>
              )}
            </Group>
            {hasSavings && (
              <Text size="sm" c="green.7" fw={500}>
                {savingsPercent}% mniej niż na {currentTariff}
              </Text>
            )}
          </Stack>
        </Group>

        <Divider />

        {/* Cost comparison */}
        <Group justify="space-between" wrap="wrap">
          <div>
            <Text size="xs" c="dimmed">Obecna taryfa ({currentTariff})</Text>
            <Text size="lg" fw={500}>{formatCurrency(currentCost)}/rok</Text>
          </div>
          <Stack ta="right" gap={0}>
            <Text size="xs" c="dimmed">Rekomendowana ({recommendedTariff})</Text>
            <Text size="lg" fw={700} c="ecoGreen.7">{formatCurrency(recommendedCost)}/rok</Text>
          </Stack>
        </Group>

        {!hasSavings && (
          <Text size="sm" c="dimmed" ta="center">
            <IconCheck size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Obecna taryfa jest już optymalna dla tego profilu zużycia
          </Text>
        )}
      </Stack>
    </Card>
  );
}
